import React, { Component } from "react";
import clsx from "clsx";

import BackofficeService from "../../services/BackofficeService";
import UtilityService from "../../services/UtilityService";
import { I } from "./common/icons";
import { SERVICES, ROLE_ORDER, ROLES } from "./backofficeConstants";
import { fmt, isComplete, hrs, share, capacityPeriods, capacityOf, escapeCsv, downloadCsv } from "./backofficeUtils";

const { alert } = UtilityService;

const BY_LABEL = { preparer: "Preparer", reviewer: "Reviewer", manager: "Manager", team: "Team", client: "Client" };

/**
 * Capacity report: budgeted (or remaining) hours per preparer / reviewer / manager / team / client
 * for a period, against each person's weekly backoffice hours. Capacity hours are editable inline.
 */
class CapacityView extends Component {
	state = { by: "preparer", periodKey: "next2", basis: "all", open: new Set(), editing: null, editValue: "" };

	// ------------------------------------------------------------------ data

	rows(useFilters) {
		const { tasks, clients, serviceFilter, passFilters } = this.props;
		const { periodKey, basis } = this.state;
		const period = capacityPeriods()[periodKey];
		const clientById = {};
		clients.forEach(c => (clientById[c.clientId] = c));
		const out = [];
		tasks.forEach(t => {
			const c = clientById[t.clientId];
			if (!c) return;
			if (useFilters && ((serviceFilter && t.service !== serviceFilter) || !passFilters(t))) return;
			const f = share(t, period.from, period.to);
			if (!f) return;
			const done = isComplete(t);
			ROLE_ORDER.forEach(role => {
				const b = t.budget[role] || 0;
				const l = t.logged[role] || 0;
				if (!b && !l) return;
				const h = basis === "open" ? (done ? 0 : Math.max(0, b - l)) : b;
				if (basis === "open" && !h) return;
				out.push({ c, t, role, who: t[ROLES[role].idField], bud: h * f, log: l * f, varDone: done ? (l - b) * f : 0, done });
			});
		});
		return out;
	}

	groups() {
		const { users, assigneeFilter, myUserId } = this.props;
		const { by, periodKey } = this.state;
		const period = capacityPeriods()[periodKey];
		const rows = this.rows(true);
		const userById = {};
		users.forEach(u => (userById[u.id] = u));
		const groups = new Map();
		const add = (key, label, cap, r, subKey, sub) => {
			if (!groups.has(key)) groups.set(key, { key, label, cap, bud: 0, log: 0, vd: 0, tasks: new Set(), subs: new Map() });
			const g = groups.get(key);
			if (!r) return;
			g.bud += r.bud;
			g.log += r.log;
			g.vd += r.varDone;
			g.tasks.add(r.t.id);
			if (!g.subs.has(subKey)) g.subs.set(subKey, Object.assign({ bud: 0, log: 0, vd: 0, tasks: new Set() }, sub));
			const sg = g.subs.get(subKey);
			sg.bud += r.bud;
			sg.log += r.log;
			sg.vd += r.varDone;
			sg.tasks.add(r.t.id);
		};
		const whoLabel = id => (userById[id] ? userById[id].name : "Unassigned");
		const teamOf = id => (userById[id] && userById[id].teams[0] ? userById[id].teams[0].name : "");

		if (ROLE_ORDER.includes(by)) {
			const assignee = assigneeFilter === "me" ? myUserId : assigneeFilter ? parseInt(assigneeFilter, 10) : null;
			users
				.filter(u => u.weeklyHours !== null && (!assignee || u.id === assignee))
				.forEach(u => add(u.id, whoLabel(u.id), capacityOf(u, period.from, period.to)));
			rows.filter(r => r.role === by).forEach(r => add(r.who || 0, whoLabel(r.who), capacityOf(userById[r.who], period.from, period.to), r, `${r.t.id}`, { label: `${r.c.name} · ${r.t.title}`, detail: SERVICES[r.t.service].label, due: r.t.dueDate, done: r.done }));
		} else if (by === "team") {
			const teams = {};
			users.forEach(u => (u.teams.length ? u.teams : [{ id: 0, name: "No team" }]).forEach(tm => (teams[tm.name] = teams[tm.name] || []).push(u)));
			Object.keys(teams).forEach(name => add(name, name, teams[name].reduce((n, u) => n + capacityOf(u, period.from, period.to), 0)));
			rows.forEach(r => {
				const k = teamOf(r.who) || "No team";
				add(k, k, 0, r, `${r.who}`, { label: whoLabel(r.who), detail: "", cap: capacityOf(userById[r.who], period.from, period.to), person: r.who });
			});
			groups.forEach(g =>
				(teams[g.key] || []).forEach(u => {
					if (!g.subs.has(`${u.id}`)) g.subs.set(`${u.id}`, { label: u.name, detail: "", cap: capacityOf(u, period.from, period.to), person: u.id, bud: 0, log: 0, vd: 0, tasks: new Set() });
				}),
			);
		} else {
			rows.forEach(r => add(r.c.id, r.c.name, 0, r, r.t.service, { label: SERVICES[r.t.service].label, detail: "" }));
		}
		return { period, groups: [...groups.values()], userById };
	}

	// ------------------------------------------------------------------ capacity edit

	startEdit = (userId, current) => this.setState({ editing: userId, editValue: current === null || current === undefined ? "" : String(current) });

	saveEdit = async () => {
		const { editing, editValue } = this.state;
		if (!editing) return;
		const hours = parseFloat(editValue);
		const result = await BackofficeService.updateCapacity({ userId: editing, weeklyHours: Number.isFinite(hours) ? hours : 0 });
		this.setState({ editing: null });
		if (!result.ok) return alert({ type: "error", text: result.error });
		alert({ type: "success", text: "Capacity saved" });
		if (this.props.onCapacityChange) this.props.onCapacityChange();
	};

	// ------------------------------------------------------------------ render helpers

	loadCell(b, cap) {
		if (!cap) return <span className="bo-muted">—</span>;
		const pct = Math.round((b / cap) * 100);
		const cls = pct > 100 ? "over" : pct >= 85 ? "near" : "ok";
		return (
			<div className="bo-load">
				<span className="bo-bar">
					<i className={cls} style={{ width: `${Math.min(100, pct)}%` }} />
				</span>
				<b className={cls === "ok" ? "" : cls}>{pct}%</b>
			</div>
		);
	}

	varCell(v) {
		if (Math.abs(v) < 0.05) return <span className="bo-muted">0.0</span>;
		return <span className={v > 0 ? "bo-var-pos" : "bo-var-neg"}>{(v > 0 ? "+" : "−") + hrs(Math.abs(v))}</span>;
	}

	who(id) {
		const u = this.props.users.find(x => x.id === id);
		return (
			<span className="bo-who">
				<span className="bo-avatar">{u ? u.initials : "?"}</span>
				{u ? u.name : "Unassigned"}
			</span>
		);
	}

	capCell(userId, capHours) {
		const { editing, editValue } = this.state;
		const u = this.props.users.find(x => x.id === userId);
		if (editing === userId) {
			return (
				<input
					className="bo-cap-edit"
					type="number"
					min="0"
					step="0.5"
					autoFocus
					value={editValue}
					onChange={e => this.setState({ editValue: e.target.value })}
					onBlur={this.saveEdit}
					onKeyDown={e => {
						if (e.key === "Enter") this.saveEdit();
						if (e.key === "Escape") this.setState({ editing: null });
					}}
					onClick={e => e.stopPropagation()}
					aria-label="Weekly backoffice hours"
				/>
			);
		}
		return (
			<span
				title={u ? `${u.weeklyHours === null ? "No weekly capacity set" : u.weeklyHours + "h per week"} · click to edit` : ""}
				style={{ cursor: u ? "pointer" : "default", textDecoration: u ? "underline dotted" : "none" }}
				onClick={e => {
					e.stopPropagation();
					if (u) this.startEdit(u.id, u.weeklyHours);
				}}
			>
				{capHours ? hrs(capHours) : u && u.weeklyHours === null ? "set" : "—"}
			</span>
		);
	}

	csv(groups, basis) {
		const lines = [["Group", "Detail", "Tasks", basis === "open" ? "Remaining h" : "Budget h", "Logged h", "Over/under done h", "Capacity h", "Load %"].map(escapeCsv).join(",")];
		groups.forEach(g => {
			lines.push([g.label, "", g.tasks.size, hrs(g.bud), hrs(g.log), hrs(g.vd), g.cap ? hrs(g.cap) : "", g.cap ? Math.round((g.bud / g.cap) * 100) : ""].map(escapeCsv).join(","));
			g.subs.forEach(sg => lines.push([g.label, sg.label, sg.tasks.size, hrs(sg.bud), hrs(sg.log), hrs(sg.vd), sg.cap ? hrs(sg.cap) : "", sg.cap ? Math.round((sg.bud / sg.cap) * 100) : ""].map(escapeCsv).join(",")));
		});
		return lines.join("\n");
	}

	render() {
		const { by, periodKey, basis, open } = this.state;
		const periods = capacityPeriods();
		const { period, groups } = this.groups();
		const tb = groups.reduce((n, g) => n + g.bud, 0);
		const tl = groups.reduce((n, g) => n + g.log, 0);
		const tc = groups.reduce((n, g) => n + g.cap, 0);
		const tv = groups.reduce((n, g) => n + g.vd, 0);
		const over = groups.filter(g => g.cap && g.bud > g.cap).length;
		const isPeople = by !== "client";
		const tiles = [
			[basis === "open" ? "Remaining hours" : "Budgeted hours", hrs(tb) + "h", period.label, ""],
			["Capacity", isPeople ? hrs(tc) + "h" : "—", isPeople ? "Backoffice hours allocated" : "Not applicable by client", ""],
			["Load", isPeople && tc ? Math.round((tb / tc) * 100) + "%" : "—", "Budgeted ÷ capacity", isPeople && tb > tc ? "warn" : ""],
			["Logged to date", hrs(tl) + "h", "Time entered against these tasks", ""],
			[by === "team" ? "Teams over capacity" : "Over capacity", isPeople ? over : "—", isPeople ? "Load above 100%" : "", over ? "warn" : ""],
			["Over / under on completed", (tv > 0 ? "+" : tv < 0 ? "−" : "") + hrs(Math.abs(tv)) + "h", tv > 0 ? "Completed work ran over budget" : "Completed work came in under budget", tv > 0 ? "amber" : ""],
		];
		const first = BY_LABEL[by];
		const second = ROLE_ORDER.includes(by) ? "Team" : by === "team" ? "Members" : "Services";
		const sorted = groups.slice().sort((a, b) => (b.cap ? b.bud / b.cap : b.bud) - (a.cap ? a.bud / a.cap : a.bud));

		return (
			<>
				<div className="bo-cap-controls">
					<div className="bo-field">
						<label htmlFor="capBy">Report by</label>
						<select id="capBy" value={by} onChange={e => this.setState({ by: e.target.value, open: new Set() })}>
							<option value="preparer">Preparer</option>
							<option value="team">Team</option>
							<option value="reviewer">Reviewer</option>
							<option value="manager">Manager</option>
							<option value="client">Client</option>
						</select>
					</div>
					<div className="bo-field">
						<label htmlFor="capPeriod">Period</label>
						<select id="capPeriod" value={periodKey} onChange={e => this.setState({ periodKey: e.target.value })}>
							{Object.keys(periods).map(k => (
								<option key={k} value={k}>
									{periods[k].label}
								</option>
							))}
						</select>
					</div>
					<div className="bo-field">
						<label htmlFor="capBasis">Hours shown</label>
						<select id="capBasis" value={basis} onChange={e => this.setState({ basis: e.target.value })}>
							<option value="all">All tasks in period</option>
							<option value="open">Open tasks only (remaining)</option>
						</select>
					</div>
					<span className="bo-spacer" />
					<button className="bo-btn" type="button" onClick={() => downloadCsv(`backoffice-capacity-${period.from}-${period.to}.csv`, this.csv(sorted, basis))}>
						<I.download />
						Download CSV
					</button>
				</div>

				<div className="bo-cap-sum">
					{tiles.map(x => (
						<div key={x[0]} className="bo-tile">
							<span className="bo-tile__lbl">{x[0]}</span>
							<span className={clsx("bo-tile__val", x[3] && `bo-tile__val--${x[3]}`)}>{x[1]}</span>
							<span className="bo-tile__sub">{x[2]}</span>
						</div>
					))}
				</div>

				<div className="bo-cap-table-wrap">
					<table className="bo-cap-table">
						<thead>
							<tr>
								<th>{first}</th>
								<th>{second}</th>
								<th className="n">Tasks</th>
								<th className="n">{basis === "open" ? "Remaining" : "Budget"} (h)</th>
								<th className="n">Logged (h)</th>
								<th className="n">Over / under on done (h)</th>
								<th className="n">Capacity (h)</th>
								<th className="n">Load</th>
							</tr>
						</thead>
						<tbody>
							{!sorted.length && (
								<tr>
									<td colSpan="8" className="bo-muted">
										No budgeted hours in this period.
									</td>
								</tr>
							)}
							{sorted.map(g => {
								const key = `${by}:${g.key}`;
								const isOpen = open.has(key);
								const user = ROLE_ORDER.includes(by) ? this.props.users.find(u => u.id === g.key) : null;
								const col2 = ROLE_ORDER.includes(by) ? (user && user.teams[0] ? user.teams[0].name : "") : by === "team" ? `${g.subs.size} people` : `${g.subs.size} services`;
								const subs = [...g.subs.values()].sort((a, b) => ((a.due || "") < (b.due || "") ? -1 : 1));
								return (
									<React.Fragment key={key}>
										<tr
											className={clsx("grp", { open: isOpen })}
											onClick={() => {
												const next = new Set(open);
												next.has(key) ? next.delete(key) : next.add(key);
												this.setState({ open: next });
											}}
										>
											<td>
												<I.chev className={clsx("bo-chev", { "bo-chev--open": isOpen })} />
												{ROLE_ORDER.includes(by) ? this.who(g.key) : <b>{g.label}</b>}
											</td>
											<td>{col2}</td>
											<td className="n">{g.tasks.size}</td>
											<td className="n">{hrs(g.bud)}</td>
											<td className="n">{hrs(g.log)}</td>
											<td className="n">{this.varCell(g.vd)}</td>
											<td className="n">{ROLE_ORDER.includes(by) ? this.capCell(g.key, g.cap) : g.cap ? hrs(g.cap) : "—"}</td>
											<td>{this.loadCell(g.bud, g.cap)}</td>
										</tr>
										{isOpen && !subs.length && (
											<tr className="sub">
												<td colSpan="8">No hours booked in this period.</td>
											</tr>
										)}
										{isOpen &&
											subs.map((sg, idx) => (
												<tr key={idx} className="sub">
													<td>
														{sg.person ? this.who(sg.person) : sg.label}
														{sg.due && (
															<span className="bo-mono bo-muted" style={{ fontSize: 11.5, marginLeft: 6 }}>
																· due {fmt(sg.due)}
																{sg.done ? " · done" : ""}
															</span>
														)}
													</td>
													<td>{sg.detail || ""}</td>
													<td className="n">{sg.tasks.size}</td>
													<td className="n">{hrs(sg.bud)}</td>
													<td className="n">{hrs(sg.log)}</td>
													<td className="n">{this.varCell(sg.vd)}</td>
													<td className="n">{sg.person ? this.capCell(sg.person, sg.cap) : sg.cap ? hrs(sg.cap) : ""}</td>
													<td>{sg.cap ? this.loadCell(sg.bud, sg.cap) : ""}</td>
												</tr>
											))}
									</React.Fragment>
								);
							})}
						</tbody>
						<tfoot>
							<tr>
								<td>Total</td>
								<td />
								<td className="n">{new Set(groups.flatMap(g => [...g.tasks])).size}</td>
								<td className="n">{hrs(tb)}</td>
								<td className="n">{hrs(tl)}</td>
								<td className="n">{this.varCell(tv)}</td>
								<td className="n">{isPeople ? hrs(tc) : "—"}</td>
								<td>{isPeople ? this.loadCell(tb, tc) : ""}</td>
							</tr>
						</tfoot>
					</table>
				</div>
				<div className="bo-cap-note">
					Each task's budget is spread evenly across the business days between its start and due date, so a 4-hour task running Mon–Thu adds 1 hour a day. Capacity is the weekly hours each person has allocated to backoffice work (click a
					capacity cell to edit it, or set it under Settings › Users), pro-rated to the period's business days. Service, assignee and status filters above apply.
				</div>
			</>
		);
	}
}

export default CapacityView;
